import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from 'react-router-dom';

import { Icon, Tooltip } from '~/components';
import { Button } from '~/components/Button';
import type { TerritoryCode } from '~/lib/territories/territories';
import { ArtistHeader } from '~/modules/artist-roster/pages/artist-details/components/ArtistHeader/ArtistHeader';
import { TerritoryPicker } from '~/modules/artist-roster/pages/track-details/components/TerritoryPicker/TerritoryPicker';
import type { ArtistDetails } from '~/modules/artist-roster/types';
import { arraysEqualUnordered } from '~/modules/artist-roster/utils/arraysEqualUnordered';

import { CampaignBenchmarksTable } from './components/CampaignBenchmarksTable';
import { ObjectivePicker } from './components/ObjectivePicker/ObjectivePicker';
import { PlatformsPicker } from './components/PlatformsPicker/PlatformsPicker';
import { useBenchmarkQuery } from './hooks/useBenchmarkQuery';
import { exportBenchmarksCSV } from './lib/benchmarks';

const BENCHMARKS_URL_PARAMS = {
  OBJECTIVE: 'objective',
  PLATFORMS: 'platforms',
  TERRITORIES: 'territories',
};

export function ArtistBenchmarksPage() {
  const { artistData } = useOutletContext<{ artistData: ArtistDetails }>();
  const { t } = useTranslation('campaign-planner', { keyPrefix: 'benchmarks' });

  const [searchParams, setSearchParams] = useSearchParams();
  const objectiveParam =
    searchParams.get(BENCHMARKS_URL_PARAMS.OBJECTIVE) || undefined;
  const platformsParam =
    searchParams.get(BENCHMARKS_URL_PARAMS.PLATFORMS) || undefined;
  const territoriesParam =
    searchParams.get(BENCHMARKS_URL_PARAMS.TERRITORIES) || undefined;

  const [objective, setObjective] = useState(objectiveParam);
  const [platforms, setPlatforms] = useState<string[]>(
    platformsParam ? platformsParam.split(',') : [],
  );
  const [selectedTerritories, setSelectedTerritories] = useState<
    TerritoryCode[]
  >(territoriesParam ? territoriesParam.split(',') : []);
  const [isExporting, setIsExporting] = useState(false);

  const {
    data: benchmarksData,
    error,
    isLoading: isLoadingBenchmarks,
    isFetching: isFetchingBenchmarks,
  } = useBenchmarkQuery({
    uaid: artistData?.uaid?.toString(),
    objective,
    platforms,
    territoryCodes: selectedTerritories,
  });

  const handleObjectiveChange = useCallback(
    (value: string) => {
      setObjective(value);
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        newParams.set(BENCHMARKS_URL_PARAMS.OBJECTIVE, value);

        return newParams;
      });
    },
    [setSearchParams],
  );

  const handlePlatformsChange = (value: string[]) => {
    if (arraysEqualUnordered(value, platforms)) {
      return;
    }

    setPlatforms(value);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      newParams.set(BENCHMARKS_URL_PARAMS.PLATFORMS, value.join(','));

      return newParams;
    });
  };

  const handleTerritoriesChange = (value: TerritoryCode[]) => {
    if (arraysEqualUnordered(value, selectedTerritories)) {
      return;
    }

    setSelectedTerritories(value);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      if (value.length === 0) {
        newParams.delete(BENCHMARKS_URL_PARAMS.TERRITORIES);
      } else {
        newParams.set(BENCHMARKS_URL_PARAMS.TERRITORIES, value.join(','));
      }

      return newParams;
    });
  };

  const handleExportButtonClick = async () => {
    if (!artistData?.name) {
      return;
    }

    if (!objective) {
      return;
    }

    setIsExporting(true);

    try {
      await exportBenchmarksCSV({
        artistName: artistData.name,
        objective,
        benchmarksData,
      });
    } finally {
      setIsExporting(false);
    }
  };

  // We need to add 'objective=awareness' to the URL but not cause a navigation
  // event which puts an entry in the browser history so use navigate() with
  // {replace: true} instead of setSearchParams().
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!objectiveParam) {
      setObjective('awareness');

      const newSearchParams = new URLSearchParams(searchParams);

      newSearchParams.set('objective', 'awareness');
      navigate(`${location.pathname}?${newSearchParams.toString()}`, {
        replace: true,
      });
    }
  }, [location.pathname, navigate, objectiveParam, searchParams]);

  if (!artistData?.uaid) {
    return (
      <div className="flex h-full items-center justify-center">
        <span>{t('errors.noUAID')}</span>
      </div>
    );
  }

  return (
    <>
      <ArtistHeader {...{ artistData }}>
        <div className="mb-lg flex items-end justify-between">
          <div className="flex flex-col">
            <header className="flex items-center gap-sm">
              <h1 className="text-body-md">{t('title')}</h1>
              <Tooltip
                content={t('titleTooltip')}
                delayDuration={0}
                side="bottom"
                sideOffset={8}
                align="end"
                size="sm"
                contentClassName="max-w-[300px] px-md py-sm"
              >
                <Icon
                  type="info-circle"
                  variant="light"
                  size="xs"
                  aria-label={t('title')}
                />
              </Tooltip>
            </header>
          </div>
        </div>
        <div className="flex items-end justify-between pb-lg">
          <div className="flex flex-1 items-center gap-md text-body-sm">
            <div className="flex max-w-sm flex-1 flex-col items-start gap-sm">
              <span>{t('labels.marketingObjective')}</span>
              <ObjectivePicker
                value={objective}
                onChange={handleObjectiveChange}
              />
            </div>
            <div className="flex max-w-sm flex-1 flex-col items-start gap-sm">
              <span>{t('labels.markets')}</span>
              <TerritoryPicker
                initialSelectedTerritories={selectedTerritories}
                onApply={handleTerritoriesChange}
                triggerClassName="w-full"
              />
            </div>
            <div className="flex max-w-sm flex-1 flex-col items-start gap-sm">
              <span>{t('labels.platforms')}</span>
              <PlatformsPicker
                value={platforms}
                onApply={handlePlatformsChange}
              />
            </div>
          </div>
          {benchmarksData?.length ? (
            <Button
              variant="text"
              className="flex items-center justify-center gap-sm font-semibold"
              onClick={handleExportButtonClick}
              disabled={isExporting}
            >
              {isExporting
                ? t('export.labels.exporting')
                : t('export.labels.export')}
              <div className="inline-block">
                <Icon type="file-export" variant="regular" size="sm" />
              </div>
            </Button>
          ) : null}
        </div>
      </ArtistHeader>
      <div className="px-xl pb-xl">
        <CampaignBenchmarksTable
          data={benchmarksData}
          error={error}
          isLoadingBenchmarks={isLoadingBenchmarks}
          isFetchingBenchmarks={isFetchingBenchmarks}
          objective={objective}
        />
      </div>
    </>
  );
}

ArtistBenchmarksPage.displayName = 'ArtistBenchmarksPage';
